import React from 'react';
import { Button } from './ui/button';
import { TrendingUp, MessageCircle, Zap, Activity, Shield, Play, CheckCircle, Users, Award, Lock } from 'lucide-react';
import { liveStats, heroStats } from '../mockData';

const Hero = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-black">
      {/* Neural Network Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(0, 255, 65, 0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Neon Glow Effects */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl"></div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          {/* Left Content */}
          <div>
            {/* Live Badge */}
            <div className="mb-8 inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full shadow-lg shadow-emerald-500/20">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
              <span className="text-emerald-400 text-sm font-semibold tracking-wide">🤖 AI ACTIVE TRADING LIVE</span>
            </div>
            
            {/* Powerful Headline */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 text-white leading-[1.1]">
              <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Maximize Profits
              </span>
              {' '}While You Sleep
            </h1>

            <p className="text-xl sm:text-2xl text-slate-300 mb-4 leading-relaxed font-light">
              AI-powered auto trading that <span className="text-emerald-400 font-semibold">never misses an opportunity.</span>
            </p>
            <p className="text-lg text-slate-400 mb-10 leading-relaxed">
              Join 1,000+ traders earning passive income with our 94.7% win rate AI algorithm. Zero emotion. Pure profit.
            </p>

            {/* Trust Signals */}
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700/50">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span className="text-slate-300 text-sm font-medium">Trusted by 1000+ Traders</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700/50">
                <Users className="w-4 h-4 text-cyan-400" />
                <span className="text-slate-300 text-sm font-medium">24/7 AI Trading</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700/50">
                <Lock className="w-4 h-4 text-blue-400" />
                <span className="text-slate-300 text-sm font-medium">Bank-Level Security</span>
              </div>
            </div>

            {/* CTA Buttons - Premium Design */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a 
                href="#pricing"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="group relative bg-gradient-to-r from-emerald-500 via-emerald-400 to-cyan-500 hover:from-emerald-400 hover:via-emerald-300 hover:to-cyan-400 text-black px-8 py-5 text-lg font-bold rounded-xl shadow-2xl shadow-emerald-500/50 hover:shadow-emerald-400/70 transition-all duration-300 text-center inline-block overflow-hidden"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <Zap className="w-5 h-5" />
                  Start Your Free AI Trading Trial
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </a>
              <a 
                href="#profit-simulator"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('profit-simulator')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="group relative border-2 border-cyan-500/50 text-white hover:bg-cyan-500/10 px-8 py-5 text-lg font-bold rounded-xl backdrop-blur-sm transition-all duration-300 flex items-center justify-center gap-3 hover:border-cyan-400"
              >
                <Play className="w-5 h-5 text-cyan-400 group-hover:scale-110 transition-transform" />
                <span>Watch Live Demo</span>
              </a>
            </div>

            {/* Secondary CTA */}
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a 
                href="https://wa.me/971554276609?text=Hello%20Marketwise%20Solutions%2C%20I%27m%20interested%20in%20your%20AI%20trading%20platform"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-emerald-400 text-sm font-medium flex items-center gap-2 transition-colors group"
              >
                <MessageCircle className="w-4 h-4 group-hover:scale-110 transition-transform" />
                Or speak with a trading expert instantly
              </a>
            </div>

            {/* Stats Row - Redesigned */}
            <div className="grid grid-cols-3 gap-6 mt-12 pt-12 border-t border-slate-800/50">
              <div className="text-center">
                <div className="text-4xl font-black bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-1">
                  {liveStats.uptime}
                </div>
                <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold">Uptime</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-black bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mb-1">
                  {liveStats.support}
                </div>
                <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold">Support</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-black bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-1">
                  {liveStats.activeUsers}
                </div>
                <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold">Active Users</div>
              </div>
            </div>
          </div>

          {/* Right Side - Premium Floating Stats Cards */}
          <div className="relative hidden lg:block h-[600px]">
            {/* Central Glow Circle with Neural Effect */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72">
              <div className="absolute inset-0 rounded-full border-2 border-emerald-500/20 animate-pulse"></div>
              <div className="absolute inset-4 rounded-full border border-cyan-500/20" style={{ animationDelay: '0.5s' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-48 h-48 rounded-full bg-gradient-to-br from-emerald-500/20 via-cyan-500/20 to-blue-600/20 flex items-center justify-center backdrop-blur-xl border border-emerald-500/30 shadow-2xl shadow-emerald-500/20">
                  <Zap className="w-24 h-24 text-emerald-400 drop-shadow-[0_0_15px_rgba(52,211,153,0.5)]" />
                </div>
              </div>
            </div>

            {/* Premium Floating Stat Cards */}
            {/* Daily Profit Card - Top Right */}
            <div className="absolute top-8 right-0 bg-gradient-to-br from-emerald-900/40 via-emerald-950/40 to-black/40 backdrop-blur-xl border border-emerald-500/30 rounded-2xl p-6 shadow-2xl shadow-emerald-500/20 min-w-[220px] hover:scale-105 transition-transform duration-300">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-slate-300 text-sm font-semibold">Daily Profit</span>
              </div>
              <div className="text-3xl font-black text-emerald-400 mb-1">{heroStats.dailyProfit.amount}</div>
              <div className="text-emerald-400/80 text-sm font-semibold">{heroStats.dailyProfit.percentage}</div>
              <div className="mt-3 h-1 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full animate-pulse" style={{ width: '78%' }}></div>
              </div>
            </div>

            {/* Active Trades Card - Middle Right */}
            <div className="absolute top-1/2 -translate-y-1/2 right-8 bg-gradient-to-br from-cyan-900/40 via-cyan-950/40 to-black/40 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-6 shadow-2xl shadow-cyan-500/20 min-w-[220px] hover:scale-105 transition-transform duration-300">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-cyan-400" />
                </div>
                <span className="text-slate-300 text-sm font-semibold">Active Trades</span>
              </div>
              <div className="text-3xl font-black text-white mb-1">{heroStats.activeTrades.count}</div>
              <div className="text-slate-400 text-sm">{heroStats.activeTrades.detail}</div>
              <div className="mt-4 flex gap-1">
                {[...Array(8)].map((_, i) => (
                  <div key={`trade-bar-${i}`} className="h-8 w-1 bg-cyan-500/30 rounded-full animate-pulse" style={{ animationDelay: `${i * 0.1}s` }}></div>
                ))}
              </div>
            </div>

            {/* Win Rate Card - Bottom Right */}
            <div className="absolute bottom-8 right-12 bg-gradient-to-br from-blue-900/40 via-blue-950/40 to-black/40 backdrop-blur-xl border border-blue-500/30 rounded-2xl p-6 shadow-2xl shadow-blue-500/20 min-w-[220px] hover:scale-105 transition-transform duration-300">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-slate-300 text-sm font-semibold">{heroStats.winRate.label}</span>
              </div>
              <div className="text-4xl font-black bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-3">
                {heroStats.winRate.percentage}
              </div>
              <div className="relative w-full bg-slate-800 rounded-full h-3 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-cyan-500 to-emerald-500 rounded-full animate-pulse" style={{ width: '94.7%' }}></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
              </div>
            </div>

            {/* Award Badge - Top Left */}
            <div className="absolute top-20 left-8 bg-gradient-to-br from-yellow-900/40 via-yellow-950/40 to-black/40 backdrop-blur-xl border border-yellow-500/30 rounded-2xl p-4 shadow-2xl shadow-yellow-500/20 hover:scale-105 transition-transform duration-300">
              <div className="flex items-center gap-2">
                <Award className="w-6 h-6 text-yellow-400" />
                <div>
                  <div className="text-xs text-slate-400 font-semibold">2024 Winner</div>
                  <div className="text-sm text-white font-bold">Best AI Trading</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </section>
  );
};

export default Hero;